import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/*
 * PR-TRIP-AUDIT Step 7 — the read-only view behind a share link.
 * No login, nothing to click that changes anything, and the link stops working
 * after 7 days. Built on a bare axios call rather than apiClient so a stale
 * token in the viewer's browser cannot bounce them to /login.
 */

const API = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Pt { t: string; lat: number; lng: number; speed: number | null }
interface Stop {
  started_at: string; ended_at: string; minutes: number;
  lat: number; lng: number; terminal: string | null; role: string | null;
}
interface Leg {
  plate: string; from_terminal: string; to_terminal: string;
  departed_at: string; arrived_at: string; km: number; matched: boolean;
  rec_vendor?: string | null; rec_item?: string | null;
  state: 'in_records' | 'unreported' | 'not_billable';
}
interface UnrepRow {
  plate: string; from_terminal: string; to_terminal: string; departed_at: string;
  km: number | null; lane_vendor: string | null; lane_item: string | null;
  est_value_sar: number | null; status: string;
}
interface Payload {
  kind: 'vehicle' | 'fleet' | 'unreported';
  plate?: string; from: string; to: string;
  points?: Pt[]; stops?: Stop[]; legs?: Leg[]; rows?: UnrepRow[];
  summary?: {
    legs: number; inRecords: number; unreported: number; notBillable: number;
    km: number; daysWithGps?: number;
  };
}

const niceDay = (s: string) =>
  new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const hhmm = (s: string) => s.slice(11, 16);
const asDur = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`);
const roleColour = (role: string | null) =>
  role === 'loading' ? '#10b981' : role === 'unloading' ? '#ef4444' : role === 'both' ? '#f59e0b' : '#94a3b8';

export default function SharedHistoryPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    let dead = false;
    axios.get<Payload>(`${API}/api/v1/vehicle-history/shared/${token}`)
      .then((r) => { if (!dead) setData(r.data); })
      .catch((e) => {
        if (dead) return;
        const code = e?.response?.data?.error;
        setErr(code === 'link_expired' ? 'This link has expired. Ask for a new one.'
          : code === 'not_found' ? 'This link is not valid.'
            : 'Could not load this report.');
      });
    return () => { dead = true; };
  }, [token]);

  useEffect(() => {
    if (!mapEl.current || mapRef.current || !data?.points?.length) return;
    const map = L.map(mapEl.current, { zoomControl: true }).setView([23.9, 45.1], 5.4);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      { maxZoom: 18, attribution: '© OpenStreetMap' }).addTo(map);
    const line = data.points.map((p) => [p.lat, p.lng] as [number, number]);
    L.polyline(line, { color: '#b8860b', weight: 3, opacity: 0.85 }).addTo(map);
    (data.stops ?? []).forEach((s, i) => {
      L.marker([s.lat, s.lng], {
        icon: L.divIcon({
          className: '',
          html: `<div style="width:26px;height:26px;border-radius:50%;background:${roleColour(s.role)};`
            + 'color:#fff;font:600 11px/26px system-ui;text-align:center;'
            + `border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${i + 1}</div>`,
          iconSize: [26, 26], iconAnchor: [13, 13],
        }),
      }).addTo(map).bindPopup(
        `<b>${i + 1}. ${s.terminal ?? 'Unnamed stop'}</b><br>`
        + `${hhmm(s.started_at)} → ${hhmm(s.ended_at)}<br>Stood for ${asDur(s.minutes)}`,
      );
    });
    map.fitBounds(L.latLngBounds(line).pad(0.12));
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, [data]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-neutral-50 p-6">
        <div className="max-w-sm text-center">
          <div className="text-[#b8860b] font-semibold tracking-wide mb-2">JASR HUB</div>
          <p className="text-neutral-700">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen grid place-items-center bg-neutral-50 text-neutral-500">Loading…</div>;
  }

  const legs = data.legs ?? [];
  const title = data.kind === 'vehicle' ? `Vehicle ${data.plate}`
    : data.kind === 'fleet' ? 'Fleet trips' : 'Unreported trips';

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="h-[3px] w-full bg-[#b8860b] mb-3" />
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <div className="text-[#b8860b] font-semibold tracking-wide text-sm">JASR HUB</div>
              <h1 className="text-xl font-semibold">{title}</h1>
            </div>
            <div className="text-sm text-neutral-500">
              {niceDay(data.from)} to {niceDay(data.to)}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5">
        {data.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            {[
              ['Legs', String(data.summary.legs)],
              ['In records', String(data.summary.inRecords)],
              ['Unreported', String(data.summary.unreported)],
              ['Not billable', String(data.summary.notBillable)],
              ['Distance', `${data.summary.km.toLocaleString('en-US')} km`],
            ].map(([l, v], i) => (
              <div key={i} className="rounded-lg border border-neutral-200 bg-white p-3 min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-neutral-500">{l}</div>
                <div className="text-lg font-semibold mt-0.5">{v}</div>
              </div>
            ))}
          </div>
        )}

        {!!data.points?.length && (
          <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden mb-4">
            <div ref={mapEl} className="h-[320px] sm:h-[420px] w-full bg-neutral-100" />
          </div>
        )}

        {data.kind === 'unreported' ? (
          <div className="rounded-lg border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-500">
                <tr>
                  <th className="text-start font-medium px-3 py-2">Plate</th>
                  <th className="text-start font-medium px-3 py-2">Departed</th>
                  <th className="text-start font-medium px-3 py-2">Route</th>
                  <th className="text-end font-medium px-3 py-2">Km</th>
                  <th className="text-start font-medium px-3 py-2">Lane</th>
                  <th className="text-end font-medium px-3 py-2">Estimate</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows ?? []).map((r, i) => (
                  <tr key={i} className="border-t border-neutral-100">
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{r.plate}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{niceDay(r.departed_at)} {hhmm(r.departed_at)}</td>
                    <td className="px-3 py-2">{r.from_terminal} → {r.to_terminal}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{r.km ?? '—'}</td>
                    <td className="px-3 py-2">{r.lane_vendor ?? '—'}
                      {r.lane_item && <span className="text-neutral-500"> · {r.lane_item}</span>}
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">
                      {r.est_value_sar == null ? '—' : `SAR ${Math.round(r.est_value_sar).toLocaleString('en-US')}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-500">
                <tr>
                  {data.kind === 'fleet' && <th className="text-start font-medium px-3 py-2">Plate</th>}
                  <th className="text-start font-medium px-3 py-2">Date</th>
                  <th className="text-start font-medium px-3 py-2">Route</th>
                  <th className="text-end font-medium px-3 py-2">Km</th>
                  <th className="text-start font-medium px-3 py-2">In records</th>
                </tr>
              </thead>
              <tbody>
                {legs.map((l, i) => (
                  <tr key={i} className={`border-t border-neutral-100 ${l.state === 'unreported' ? 'bg-rose-50' : ''}`}>
                    {data.kind === 'fleet' && <td className="px-3 py-2 font-medium whitespace-nowrap">{l.plate}</td>}
                    <td className="px-3 py-2 whitespace-nowrap">
                      {niceDay(l.departed_at)} <span className="text-neutral-500">{hhmm(l.departed_at)}</span>
                    </td>
                    <td className="px-3 py-2">{l.from_terminal} → {l.to_terminal}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{l.km ?? 0}</td>
                    <td className="px-3 py-2">
                      {l.state === 'in_records' && <span className="text-emerald-700">{l.rec_vendor ?? 'yes'}</span>}
                      {l.state === 'unreported' && <span className="text-rose-700 font-semibold">UNREPORTED</span>}
                      {l.state === 'not_billable' && <span className="text-neutral-500">not billable</span>}
                    </td>
                  </tr>
                ))}
                {!legs.length && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-neutral-500">
                    No trips in this period.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-neutral-500 mt-4">
          A trip is a move between two real loading or unloading sites. Rest stops, fuel stops and
          waiting areas are part of the journey, not separate trips. &ldquo;Not billable&rdquo; means the
          records have never billed that lane, so no row is due. This link is read-only and expires
          7 days after it was created.
        </p>
      </main>
    </div>
  );
}
